import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Activity, LogIn, Plus, Pencil, Trash2, Filter } from "lucide-react";

const ACTION_ICONS = {
  login: LogIn,
  create: Plus,
  update: Pencil,
  delete: Trash2,
};
const ACTION_COLORS = {
  login: "text-gold",
  create: "text-emerald-700",
  update: "text-amber-700",
  delete: "text-red-600",
};

const ACTIONS = ["all", "login", "create", "update", "delete"];

export default function AdminActivity() {
  const [items, setItems] = useState([]);
  const [action, setAction] = useState("all");
  const [emailFilter, setEmailFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (action !== "all") params.action = action;
    if (emailFilter.trim()) params.admin_email = emailFilter.trim();
    const { data } = await api.get("/admin/activity", { params });
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, emailFilter]);

  const admins = useMemo(() => Array.from(new Set(items.map((i) => i.admin_email))).filter(Boolean), [items]);

  return (
    <div className="space-y-6" data-testid="admin-activity-page">
      <div>
        <div className="overline mb-2">Audit trail</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-ink-900">Admin activity</h1>
        <p className="text-ink-600 text-sm mt-2">Every admin sign-in and change — who did what, when.</p>
      </div>

      <div className="glass rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-ink-500" />
        <div className="flex flex-wrap gap-1">
          {ACTIONS.map((a) => (
            <button key={a} onClick={() => setAction(a)}
              className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider transition-colors ${action === a ? "bg-gold text-ink-900" : "text-ink-600 hover:text-ink-900 hover:bg-ink-900/[0.04]"}`}
              data-testid={`activity-filter-${a}`}>{a}</button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px]">
          <input value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} placeholder="Filter by admin email..."
            className="w-full rounded-lg bg-cream-50 border border-ink-900/15 px-3 py-1.5 outline-none focus:border-gold text-ink-900 text-sm"
            data-testid="activity-filter-email" />
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-900/[0.04]">
            <tr className="text-left text-ink-600">
              <th className="p-3">When</th>
              <th>Who</th>
              <th>Action</th>
              <th>Target</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={5} className="p-8 text-center text-ink-500">Loading…</td></tr>)}
            {!loading && items.length === 0 && (<tr><td colSpan={5} className="p-8 text-center text-ink-500">No activity yet.</td></tr>)}
            {items.map((it) => {
              const Icon = ACTION_ICONS[it.action] || Activity;
              return (
                <tr key={it.activity_id} className="border-t border-ink-900/10" data-testid={`activity-row-${it.activity_id}`}>
                  <td className="p-3 text-ink-500 text-xs whitespace-nowrap">{new Date(it.created_at).toLocaleString()}</td>
                  <td>
                    <div className="text-ink-900 font-medium">{it.admin_name}</div>
                    <div className="text-ink-500 text-xs">{it.admin_email}</div>
                  </td>
                  <td>
                    <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider ${ACTION_COLORS[it.action] || "text-ink-700"}`}>
                      <Icon className="w-3.5 h-3.5" /> {it.action}
                    </span>
                  </td>
                  <td>
                    <div className="text-ink-800 capitalize">{it.target_type || "—"}</div>
                    <div className="text-ink-500 text-xs truncate max-w-[240px]">{it.target_label || it.target_id}</div>
                  </td>
                  <td className="text-ink-500 text-xs max-w-[220px] truncate">{it.details && Object.keys(it.details).length ? JSON.stringify(it.details) : ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {admins.length > 0 && (
        <p className="text-xs text-ink-500">Admins in this view: {admins.join(", ")}</p>
      )}
    </div>
  );
}
